import { useMemo, useState, type CSSProperties, type ReactNode } from "react";

export type MotionDiscipline = "universal" | "gaming" | "coding" | "cad" | "editing" | "docs";
export type MotionStyle = "signal" | "ember" | "paper";
export type MotionFamily = "brand" | "observe" | "evidence" | "review" | "stage";
export type MotionComponentId =
  | "logo-resolve"
  | "observe-window"
  | "evidence-thread"
  | "insight-resolve"
  | "layer-stack"
  | "review-loop";

type MotionContext = {
  discipline: MotionDiscipline;
  style: MotionStyle;
  playing: boolean;
  speed: number;
  compact?: boolean;
};

export type MotionCatalogEntry = {
  id: MotionComponentId;
  family: MotionFamily;
  title: string;
  purpose: string;
  agentPrompt: string;
  render: (context: MotionContext) => ReactNode;
};

const disciplineLabels: Record<MotionDiscipline, string> = {
  universal: "Every discipline",
  gaming: "Gaming",
  coding: "Agentic coding",
  cad: "CAD",
  editing: "Video editing",
  docs: "Docs and sheets",
};

const disciplineSignals: Record<MotionDiscipline, string[]> = {
  universal: ["context", "sequence", "timing"],
  gaming: ["round state", "input timing", "decision window"],
  coding: ["editor state", "tool path", "implementation"],
  cad: ["viewport", "layers", "dimensions"],
  editing: ["timeline", "revisions", "pacing"],
  docs: ["document", "selection", "revision"],
};

const styleLabels: Record<MotionStyle, { title: string; description: string }> = {
  signal: { title: "Signal", description: "Ink, warm-white, and coral focus." },
  ember: { title: "Ember", description: "A warmer cinematic treatment for launches." },
  paper: { title: "Paper", description: "A quiet editorial treatment for explainers." },
};

const renderLogoResolve = ({ compact }: MotionContext) => (
  <div className={`motion-primitive motion-logo-resolve ${compact ? "is-compact" : ""}`} role="img" aria-label="Hindset mark resolving into a wordmark">
    <div className="motion-logo-lockup">
      <img src="/brand/hindset-mark.svg" alt="" />
      <span className="motion-wordmark">h<span className="motion-wordmark-i">ı</span>ndset</span>
    </div>
  </div>
);

function WindowBar({ label }: { label: string }) {
  return <div className="motion-window-bar"><span className="motion-window-dots" aria-hidden="true"><i /><i /><i /></span><span>{label}</span><span className="motion-window-status">local fixture</span></div>;
}

const renderObserveWindow = ({ discipline, compact }: MotionContext) => {
  const signals = disciplineSignals[discipline];
  return (
    <div className={`motion-primitive motion-observe-window ${compact ? "is-compact" : ""}`} role="img" aria-label={`${disciplineLabels[discipline]} observation window`}>
      <div className="motion-window-frame">
        <WindowBar label={`${disciplineLabels[discipline]} / observe`} />
        <div className="motion-window-content">
          <div className="motion-window-sidebar" aria-hidden="true">
            <span className="is-selected" />
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="motion-window-canvas">
            <div className="motion-window-heading"><span>{signals[0]}</span><strong>One moment in context.</strong></div>
            <div className="motion-window-tiles" aria-hidden="true"><i /><i /><i /></div>
            <div className="motion-window-timeline" aria-hidden="true"><span /><span /><span className="is-active" /><span /><span /><span /><b /></div>
            <span className="motion-window-focus" aria-hidden="true" />
            <span className="motion-window-cursor" aria-hidden="true" />
          </div>
        </div>
      </div>
    </div>
  );
};

const renderEvidenceThread = ({ discipline, compact }: MotionContext) => {
  const signals = disciplineSignals[discipline];
  return (
    <div className={`motion-primitive motion-evidence-thread ${compact ? "is-compact" : ""}`} role="img" aria-label="Evidence thread connecting context, sequence, and timing">
      <div className="motion-evidence-head"><span>evidence thread</span><strong>{disciplineLabels[discipline]}</strong></div>
      <div className="motion-evidence-events" aria-hidden="true">
        {signals.map((signal, index) => <div key={signal} className={`motion-evidence-event ${index === 1 ? "is-emphasis" : ""}`}><span>0{index + 1}</span><strong>{signal}</strong><i /></div>)}
      </div>
      <div className="motion-evidence-timeline" aria-hidden="true"><i /><i /><i className="is-active" /><i /><i /><b /></div>
    </div>
  );
};

const renderInsightResolve = ({ discipline, compact }: MotionContext) => (
    <div className={`motion-primitive motion-insight-resolve ${compact ? "is-compact" : ""}`} role="img" aria-label="Evidence cards resolving into one review insight">
    <div className="motion-insight-source motion-insight-source-one"><span>01</span><strong>{disciplineSignals[discipline][0]}</strong><i /></div>
    <div className="motion-insight-source motion-insight-source-two"><span>02</span><strong>{disciplineSignals[discipline][1]}</strong><i /></div>
    <div className="motion-insight-source motion-insight-source-three"><span>03</span><strong>{disciplineSignals[discipline][2]}</strong><i /></div>
    <div className="motion-insight-core"><span>review</span><strong>Carry this forward.</strong><i /></div>
  </div>
);

const renderLayerStack = ({ discipline, compact }: MotionContext) => (
  <div className={`motion-primitive motion-layer-stack ${compact ? "is-compact" : ""}`} role="img" aria-label="Layered session context cards">
    <div className="motion-layer motion-layer-back"><span>{disciplineSignals[discipline][0]}</span><i /><i /><i /></div>
    <div className="motion-layer motion-layer-mid"><span>{disciplineSignals[discipline][1]}</span><i /><i /><i /></div>
    <div className="motion-layer motion-layer-front"><span>{disciplineSignals[discipline][2]}</span><strong>Context stays attached.</strong><b /></div>
  </div>
);

const renderReviewLoop = ({ discipline, compact }: MotionContext) => (
  <div className={`motion-primitive motion-review-loop ${compact ? "is-compact" : ""}`} role="img" aria-label="A review loop returning from a finding to the next attempt">
    <div className="motion-review-ground" aria-hidden="true" />
    <div className="motion-review-sheet"><span>review complete</span><strong>See what changed.</strong><i /></div>
    <div className="motion-review-next"><span>{disciplineLabels[discipline]}</span><strong>Make the next attempt useful.</strong><i /></div>
    <div className="motion-review-handoff">return to work</div>
  </div>
);

const renderers: Record<MotionComponentId, (context: MotionContext) => ReactNode> = {
  "logo-resolve": renderLogoResolve,
  "observe-window": renderObserveWindow,
  "evidence-thread": renderEvidenceThread,
  "insight-resolve": renderInsightResolve,
  "layer-stack": renderLayerStack,
  "review-loop": renderReviewLoop,
};

export const MOTION_CATALOG: readonly MotionCatalogEntry[] = [
  { id: "logo-resolve", family: "brand", title: "Signal resolve", purpose: "Bring the mark from a quiet field into a legible Hindset lockup.", agentPrompt: "Use a restrained mark trace that ends in a clear Hindset lockup.", render: renderers["logo-resolve"] },
  { id: "observe-window", family: "observe", title: "Observation window", purpose: "Show the work continuing while Hindset stays out of the interaction path.", agentPrompt: "Show the selected discipline in motion without implying shipped capture or analysis.", render: renderers["observe-window"] },
  { id: "evidence-thread", family: "evidence", title: "Evidence thread", purpose: "Connect context, sequence, and timing to one meaningful moment.", agentPrompt: "Animate one grounded evidence path; never add unsupported scores or certainty.", render: renderers["evidence-thread"] },
  { id: "insight-resolve", family: "review", title: "Insight resolve", purpose: "Collapse several signals into one clear next-step card.", agentPrompt: "Resolve evidence into a review prompt, not a claim of automated coaching accuracy.", render: renderers["insight-resolve"] },
  { id: "layer-stack", family: "stage", title: "Context stack", purpose: "Give a feature story depth by keeping related context physically connected.", agentPrompt: "Layer contextual surfaces with one focal plane and a quiet background.", render: renderers["layer-stack"] },
  { id: "review-loop", family: "review", title: "Return loop", purpose: "Make the product arc legible: work, review, then another attempt.", agentPrompt: "Close the story by returning from a finding to the next attempt.", render: renderers["review-loop"] },
];

const sequence: MotionComponentId[] = ["logo-resolve", "observe-window", "evidence-thread", "insight-resolve", "review-loop"];

const familyLabels: Record<MotionFamily | "all", string> = {
  all: "All pieces",
  brand: "Brand",
  observe: "Observe",
  evidence: "Evidence",
  review: "Review",
  stage: "Stage",
};

const disciplineOptions = Object.keys(disciplineLabels) as MotionDiscipline[];
const styleOptions = Object.keys(styleLabels) as MotionStyle[];
const familyOptions: (MotionFamily | "all")[] = ["all", "brand", "observe", "evidence", "review", "stage"];

function MotionPrimitive({ id, context }: { id: MotionComponentId; context: MotionContext }) {
  const stateClass = context.compact ? "is-static" : context.playing ? "is-playing" : "is-paused";
  return <div className={`motion-primitive-wrap motion-${id} ${stateClass}`} style={{ "--motion-speed": context.speed } as CSSProperties}>{renderers[id](context)}</div>;
}

export default function MotionLabGallery() {
  const [activeId, setActiveId] = useState<MotionComponentId>("logo-resolve");
  const [family, setFamily] = useState<MotionFamily | "all">("all");
  const [discipline, setDiscipline] = useState<MotionDiscipline>("universal");
  const [style, setStyle] = useState<MotionStyle>("signal");
  const [speed, setSpeed] = useState(1);
  const [playing, setPlaying] = useState(true);
  const [copied, setCopied] = useState(false);

  const visibleCatalog = useMemo(() => family === "all" ? MOTION_CATALOG : MOTION_CATALOG.filter(item => item.family === family), [family]);
  const active = MOTION_CATALOG.find(item => item.id === activeId) ?? MOTION_CATALOG[0];
  const sceneSpec = JSON.stringify({
    version: 1,
    scene: "observe-to-insight",
    status: "concept-fixture",
    discipline,
    style,
    durationSeconds: 8,
    speed,
    beats: sequence.map((id, index) => ({ at: [0, 1.1, 3.1, 5.2, 7.1][index], component: id })),
  }, null, 2);

  const selectComponent = (id: MotionComponentId) => {
    setActiveId(id);
    setPlaying(true);
  };

  const copyScene = async () => {
    try {
      await navigator.clipboard.writeText(sceneSpec);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const context: MotionContext = { discipline, style, playing, speed };
  const compactContext: MotionContext = { discipline, style, playing: false, speed: 1, compact: true };

  return <div className="motion-lab-page">
    <section className="motion-lab-hero" aria-labelledby="motion-lab-title">
      <div className="motion-lab-hero-copy">
        <p className="motion-lab-kicker">Internal production workbench</p>
        <h1 id="motion-lab-title">Build the motion language before the product film.</h1>
        <p className="motion-lab-intro">A reusable vocabulary for Hindset scenes, feature loops, and future launch films. The same story can move through gaming, coding, CAD, editing, documents, or any other discipline.</p>
        <div className="motion-lab-notice"><span aria-hidden="true" />Concept fixtures only. This gallery does not represent shipped capture, analysis, or coaching behavior.</div>
      </div>
      <div className="motion-lab-hero-stage motion-style-signal"><MotionPrimitive id="logo-resolve" context={{ ...context, style: "signal" }} /></div>
    </section>

    <section className="motion-lab-workbench" aria-labelledby="motion-catalog-title">
      <div className="motion-lab-section-heading"><div><p className="motion-lab-kicker">Catalog</p><h2 id="motion-catalog-title">Reusable pieces, assembled into scenes.</h2></div><p>Choose a primitive, preview it under a discipline skin, then hand the scene spec to an agent or renderer.</p></div>
      <div className="motion-lab-workbench-grid">
        <aside className="motion-lab-catalog-panel" aria-label="Motion component catalog">
          <div className="motion-lab-panel-heading"><span>Motion catalog</span><strong>{MOTION_CATALOG.length} primitives</strong></div>
          <div className="motion-lab-filter-row" role="group" aria-label="Filter motion components">{familyOptions.map(option => <button key={option} type="button" className={family === option ? "is-selected" : ""} aria-pressed={family === option} onClick={() => setFamily(option)}>{familyLabels[option]}</button>)}</div>
          <div className="motion-lab-catalog-list">{visibleCatalog.map(item => <button key={item.id} type="button" className={`motion-lab-catalog-item ${item.id === activeId ? "is-selected" : ""}`} aria-pressed={item.id === activeId} onClick={() => selectComponent(item.id)}><span className="motion-lab-item-index">{String(MOTION_CATALOG.indexOf(item) + 1).padStart(2, "0")}</span><span><strong>{item.title}</strong><small>{item.family} / {item.purpose}</small></span><span className="motion-lab-item-arrow" aria-hidden="true">↗</span></button>)}</div>
        </aside>

        <div className="motion-lab-preview-panel">
          <div className="motion-lab-preview-toolbar"><div><span>Live component preview</span><strong>{active.title}</strong></div><div className="motion-lab-toolbar-controls"><button type="button" className="motion-lab-play" aria-pressed={playing} onClick={() => setPlaying(value => !value)}>{playing ? "Pause" : "Play"}</button><span className="motion-lab-speed-label">Speed</span>{[.7, 1, 1.4].map(value => <button key={value} type="button" className={speed === value ? "is-selected" : ""} aria-pressed={speed === value} onClick={() => setSpeed(value)}>{value}×</button>)}</div></div>
          <div key={`${active.id}-${style}-${discipline}`} className={`motion-lab-canvas motion-style-${style} ${playing ? "is-playing" : "is-paused"}`} aria-label={`${active.title} preview`} style={{ "--motion-speed": speed } as CSSProperties}><MotionPrimitive id={active.id} context={context} /></div>
          <div className="motion-lab-preview-foot"><div><span>Purpose</span><strong>{active.purpose}</strong></div><div><span>Agent instruction</span><strong>{active.agentPrompt}</strong></div></div>
        </div>
      </div>
    </section>

    <section className="motion-lab-controls" aria-labelledby="motion-controls-title">
      <div className="motion-lab-section-heading"><div><p className="motion-lab-kicker">Parameters</p><h2 id="motion-controls-title">Change the skin, keep the meaning.</h2></div><p>Discipline changes the evidence vocabulary. Style changes the treatment. The underlying motion grammar stays consistent.</p></div>
      <div className="motion-lab-parameter-grid">
        <div className="motion-lab-parameter"><span>Discipline skin</span><div className="motion-lab-option-row">{disciplineOptions.map(option => <button key={option} type="button" className={discipline === option ? "is-selected" : ""} aria-pressed={discipline === option} onClick={() => setDiscipline(option)}>{disciplineLabels[option]}</button>)}</div></div>
        <div className="motion-lab-parameter"><span>Visual treatment</span><div className="motion-lab-option-row">{styleOptions.map(option => <button key={option} type="button" className={style === option ? "is-selected" : ""} aria-pressed={style === option} onClick={() => setStyle(option)}><i className={`motion-style-swatch swatch-${option}`} aria-hidden="true" />{styleLabels[option].title}</button>)}</div><small>{styleLabels[style].description}</small></div>
      </div>
    </section>

    <section className="motion-lab-sequence" aria-labelledby="motion-sequence-title">
      <div className="motion-lab-section-heading"><div><p className="motion-lab-kicker">Scene grammar</p><h2 id="motion-sequence-title">Compose one story from approved beats.</h2></div><p>Agents should choose from this catalog and emit a validated scene description. They should not invent a new product language for every video.</p></div>
      <div className="motion-sequence-track">{sequence.map((id, index) => { const item = MOTION_CATALOG.find(entry => entry.id === id)!; return <div className="motion-sequence-step" key={id}><button type="button" onClick={() => selectComponent(id)} className={id === activeId ? "is-selected" : ""}><span>{String(index + 1).padStart(2, "0")}</span><strong>{item.title}</strong><small>{item.family}</small></button>{index < sequence.length - 1 && <i aria-hidden="true" />}</div>; })}</div>
      <div className="motion-agent-handoff"><div className="motion-agent-copy"><p className="motion-lab-kicker">Agent handoff</p><h3>A small, inspectable contract.</h3><p>The scene description is intentionally boring JSON. It gives an LLM room to compose while the catalog, timing, brand rules, and fixture boundaries remain enforceable.</p><button type="button" onClick={copyScene}>{copied ? "Copied scene spec" : "Copy scene spec"}</button></div><pre aria-label="Generated scene specification"><code>{sceneSpec}</code></pre></div>
    </section>

    <section className="motion-lab-gallery" aria-labelledby="motion-gallery-title">
      <div className="motion-lab-section-heading"><div><p className="motion-lab-kicker">Reference shelf</p><h2 id="motion-gallery-title">See the pieces without starting a timeline.</h2></div><p>Static thumbnails stay quiet; the selected stage above carries the motion so the gallery remains readable.</p></div>
      <div className="motion-lab-card-grid">{MOTION_CATALOG.map(item => <button key={item.id} type="button" className={`motion-lab-card ${item.id === activeId ? "is-selected" : ""}`} onClick={() => selectComponent(item.id)}><div className={`motion-lab-card-preview motion-style-${style}`}><MotionPrimitive id={item.id} context={compactContext} /></div><div className="motion-lab-card-copy"><span>{item.family}</span><strong>{item.title}</strong><small>{item.purpose}</small></div></button>)}</div>
    </section>
  </div>;
}
